import React, { useCallback, useMemo, useState } from 'react'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import { UserRole, User } from '@/types'
import { ActorUsersPage } from '@/components/users/ActorUsersPage'
import { WholesalerResellersDialog } from '@/components/users/WholesalerResellersDialog'
import { useWholesalersStore } from '@/stores/wholesalers.store'
import { useAuth } from '@/hooks/useAuth'
import { ActorApkModal } from '@/components/apk/ActorApkModal'
import { createApkColumn } from '@/components/apk/createApkColumn'
import { canManageApk, canManageWholesalers } from '@/utils/role-access'

export const WholesalersPage: React.FC = () => {
  const wholesalers = useWholesalersStore(state => state.wholesalers)
  const fetchWholesalers = useWholesalersStore(state => state.fetchWholesalers)
  const addWholesaler = useWholesalersStore(state => state.addWholesaler)
  const updateWholesaler = useWholesalersStore(state => state.updateWholesaler)
  const deleteWholesaler = useWholesalersStore(state => state.deleteWholesaler)
  const { user } = useAuth()
  const [selectedWholesaler, setSelectedWholesaler] = useState<User | null>(null)
  const [apkActor, setApkActor] = useState<User | null>(null)

  const handleApkClick = useCallback((actor: User) => {
    setApkActor(actor)
  }, [])

  const apkColumn = useMemo(() => createApkColumn(handleApkClick), [handleApkClick])

  const handleAttachedResellersClick = useCallback((wholesaler: User) => {
    setSelectedWholesaler(wholesaler)
  }, [])

  const attachedResellersColumn: ColDef = useMemo(
    () => ({
      field: 'attachedResellersCount',
      headerName: 'Revendeurs rattachés',
      width: 180,
      minWidth: 180,
      sortable: true,
      valueGetter: params => params.data?.attachedResellersCount ?? 0,
      cellRenderer: (params: ICellRendererParams) => {
        const count = params.value ?? 0
        return (
          <button
            type="button"
            onClick={() => handleAttachedResellersClick(params.data)}
            disabled={count === 0}
            className={`font-medium ${
              count > 0
                ? 'text-[var(--color-primary)] hover:underline cursor-pointer'
                : 'text-[var(--color-text-muted)] cursor-default'
            }`}
          >
            {count}
          </button>
        )
      },
    }),
    [handleAttachedResellersClick]
  )

  return (
    <>
      <ActorUsersPage
        title="Liste des grossistes"
        createButtonLabel="Nouveau grossiste"
        gridCollection="wholesalers"
        users={wholesalers}
        fetchUsers={fetchWholesalers}
        onCreate={data => addWholesaler({ ...data, role: UserRole.WHOLESALER })}
        onUpdate={updateWholesaler}
        onDelete={deleteWholesaler}
        canManage={canManageWholesalers(user)}
        fixedRole={UserRole.WHOLESALER}
        hideRoleColumn
        extraColumns={[attachedResellersColumn, apkColumn]}
        createTitle="Nouveau grossiste"
        editTitle="Modifier le grossiste"
        autoGeneratePassword
        requireBillingFields
        showBrandingFields
        showActorColumns
        showLoginAs={true}
      />

      {selectedWholesaler && (
        <WholesalerResellersDialog
          wholesaler={selectedWholesaler}
          onClose={() => setSelectedWholesaler(null)}
        />
      )}

      {apkActor && (
        <ActorApkModal
          actor={apkActor}
          canManage={canManageApk(user)}
          onClose={() => setApkActor(null)}
        />
      )}
    </>
  )
}
